import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Grid, Paper, Alert,
} from '@mui/material';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { useDataProvider } from 'react-admin';
import { buildTransitionChain } from '@/components/workflow/machineHelpers';
import { useRuleEvaluation } from '@/components/workflow/useRuleEvaluation';

const STEPS = [
  { id: 'org', title: 'Organisation Details' },
  { id: 'contact', title: 'Contact Information' },
  { id: 'preferences', title: 'Service Preferences' },
  { id: 'review', title: 'Review & Submit' },
];

interface FormValues {
  organisationName: string;
  registrationNumber: string;
  organisationType: string;
  industry: string;
  contactPerson: string;
  contactEmail: string;
  contactPhone: string;
  physicalAddress: string;
  preferredServices: string[];
  billingCycle: string;
  notes: string;
}

const INITIAL_VALUES: FormValues = {
  organisationName: '',
  registrationNumber: '',
  organisationType: '',
  industry: '',
  contactPerson: '',
  contactEmail: '',
  contactPhone: '',
  physicalAddress: '',
  preferredServices: [],
  billingCycle: '',
  notes: '',
};

const ORG_TYPES = ['Private Company', 'Non-Profit', 'Government', 'Sole Proprietorship', 'Partnership'];
const BILLING_CYCLES = ['Monthly', 'Quarterly', 'Annually'];

const stepDescriptions: Record<string, string> = {
  org: "Enter the client organisation's details. Clients are typically schools, training centres, or corporate entities.",
  contact: "Enter the primary contact person for this client organisation.",
  preferences: "Set service preferences and billing terms for this client.",
  review: "Review all information before creating the client record.",
};

export const ClientWizard = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const evaluateRef = useRef<(() => Promise<{ allowed: boolean; reason?: string }>) | null>(null);

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    if (evaluateRef.current) {
      const ruleResult = await evaluateRef.current();
      if (!ruleResult.allowed) {
        throw new Error(ruleResult.reason || 'Rule evaluation failed');
      }
    }

    const entityResult = await dataProvider.create('tutoring-clients', {
      data: {
        organisationName: v.organisationName,
        registrationNumber: v.registrationNumber,
        organisationType: v.organisationType,
        industry: v.industry,
        contactPerson: v.contactPerson,
        contactEmail: v.contactEmail,
        contactPhone: v.contactPhone,
        physicalAddress: v.physicalAddress,
        preferredServices: v.preferredServices,
        billingCycle: v.billingCycle,
        notes: v.notes,
        status: 'Draft',
      },
    });

    const entityId = entityResult.data.id;
    const { finalState, transitions } = buildTransitionChain('client', 'draft', ['SUBMIT']);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'client',
        entityId,
        entityName: v.organisationName,
        currentState: { value: finalState, context: v },
        status: 'active',
        transitions,
      },
    });

    return { id: entityId };
  }, [dataProvider]);

  const {
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep,
    isSaving, isCompleted, progress, values, errors, stepFeedback,
    nextStep, prevStep, updateValues, complete, reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
    stepValidators: {
      org: (vals) => {
        const v = vals as unknown as FormValues;
        const errs: Record<string, string> = {};
        if (!v.organisationName?.trim()) errs.organisationName = 'Organisation name is required';
        return { isValid: Object.keys(errs).length === 0, errors: errs };
      },
      contact: (vals) => {
        const v = vals as unknown as FormValues;
        const errs: Record<string, string> = {};
        if (!v.contactPerson?.trim()) errs.contactPerson = 'Contact person is required';
        return { isValid: Object.keys(errs).length === 0, errors: errs };
      },
    },
  });

  const handleNext = () => {
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value });
  };

  const vals = values as unknown as FormValues;
  const { evaluate } = useRuleEvaluation({ ruleId: 'client:create', context: vals as unknown as Record<string, unknown> });
  useEffect(() => {
    evaluateRef.current = evaluate;
  }, [evaluate]);

  const renderFormFields = () => {
    switch (currentStep.id) {
      case 'org':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Organisation Name" value={vals.organisationName} onChange={e => updateField('organisationName', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Registration Number" value={vals.registrationNumber} onChange={e => updateField('registrationNumber', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Organisation Type</InputLabel>
                <Select value={vals.organisationType} label="Organisation Type" onChange={e => updateField('organisationType', e.target.value)}>
                  {ORG_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Industry" value={vals.industry} onChange={e => updateField('industry', e.target.value)} fullWidth />
            </Grid>
          </Grid>
        );
      case 'contact':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Contact Person" value={vals.contactPerson} onChange={e => updateField('contactPerson', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Contact Email" value={vals.contactEmail} onChange={e => updateField('contactEmail', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Contact Phone" value={vals.contactPhone} onChange={e => updateField('contactPhone', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Physical Address" value={vals.physicalAddress} onChange={e => updateField('physicalAddress', e.target.value)} fullWidth multiline rows={2} />
            </Grid>
          </Grid>
        );
      case 'preferences':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Billing Cycle</InputLabel>
                <Select value={vals.billingCycle} label="Billing Cycle" onChange={e => updateField('billingCycle', e.target.value)}>
                  {BILLING_CYCLES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Notes" value={vals.notes} onChange={e => updateField('notes', e.target.value)} fullWidth multiline rows={3} />
            </Grid>
          </Grid>
        );
      case 'review':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Review client details before creating. A workflow will be initialized in Draft state.</Alert>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Organisation</Typography>
                <Typography variant="body2"><strong>Name:</strong> {vals.organisationName}</Typography>
                <Typography variant="body2"><strong>Reg No:</strong> {vals.registrationNumber}</Typography>
                <Typography variant="body2"><strong>Type:</strong> {vals.organisationType}</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Contact</Typography>
                <Typography variant="body2"><strong>Person:</strong> {vals.contactPerson}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {vals.contactEmail}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {vals.contactPhone}</Typography>
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={() => {}}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Client Created"
          processId={(values as { id: string }).id}
          fields={[
            { label: 'Organisation', value: vals.organisationName },
            { label: 'Contact', value: vals.contactPerson },
            { label: 'Billing', value: vals.billingCycle },
          ]}
          footerActions={[
            { label: 'View Clients', to: '/tutoring-clients' },
            { label: 'Create Another', onClick: reset },
          ]}
        />
      ) : (
        <WizardStep
          stepNumber={currentStepIndex + 1}
          totalSteps={totalSteps}
          title={currentStep.title}
          description={stepDescriptions[currentStep.id]}
        >
          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
          {renderFormFields()}
        </WizardStep>
      )}
    </WizardLayout>
  );
};
